import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    Archive,
    ArchiveRestore,
    Edit,
    FileDown,
    MapPin,
    Plus,
    Search,
    User,
    Warehouse,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Branch } from '../../types/lists.types';
import { Yard, YardStatus } from '../../types/stock.types';
import { branchesApi } from '../../utils/lists-api';
import { yardsApi } from '../../utils/stock-api';
import CloseYardDialog from './components/CloseYardDialog';
import YardManagers from './components/YardManagers';

/**
 * Yards — STOCK Settings (STOCK.md §9) and Closed Yards (§10).
 *
 * One page with an Open/Closed tab pair rather than the two separate sidebar nodes the
 * spec's tree implies. Closed Yards is the same list filtered, and splitting it across
 * two routes would duplicate every action for no gain.
 *
 * Creating and editing happen on their own routes (YardFormPage), not in a dialog — a
 * yard is a real record, so it gets a URL that survives a reload.
 */
export default function YardsPage() {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.MANAGE, RESOURCES.STOCK_ADMIN);

    const [yards, setYards] = useState<Yard[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<YardStatus>('open');
    const [search, setSearch] = useState('');
    const [closing, setClosing] = useState<Yard | null>(null);

    const fetchYards = useCallback(async () => {
        try {
            setLoading(true);
            setYards(await yardsApi.list({ status }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load yards');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        if (canRead) fetchYards();
    }, [canRead, fetchYards]);

    // Only to tell "no yards yet" apart from "no branches to hang one on" in the empty
    // state — the form page fetches its own selector options. Active branches only (§2.1).
    useEffect(() => {
        if (!canRead) return;
        branchesApi.list({ status: 'active' }).then(setBranches).catch(() => undefined);
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return yards;
        return yards.filter((y) =>
            [y.yardName, y.yardCode, y.address, y.branch?.name,
                ...(y.managers ?? []).map((m) => m.name)]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [yards, search]);

    const openAdd = () => navigate('/stock/settings/yards/new');
    const openEdit = (yard: Yard) => navigate(`/stock/settings/yards/${yard.id}/edit`);

    const handleReopen = async (yard: Yard) => {
        try {
            await yardsApi.reopen(yard.id);
            toast.success(`${yard.yardName} reopened`);
            fetchYards();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to reopen yard');
        }
    };

    /*
     * Yards are never deleted from this screen — Close is the only way out. A yard is
     * referenced by order numbers and history, so removing one is not a user-level
     * action even when the record count happens to be zero.
     */

    const handleExport = async (format: 'csv' | 'excel') => {
        try {
            await yardsApi.export(format);
            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch {
            toast.error('Failed to generate export file');
        }
    };

    if (!canRead) return <AccessRestricted />;

    const isClosed = status === 'closed';

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    {/* Yards is one section of STOCK Settings — name the parent so the
                        page does not read as if Settings and Yards were the same thing. */}
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Settings
                    </p>
                    <h1 className="text-2xl font-bold mt-0.5">Yards</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Physical stock-holding locations. Every order belongs to a yard.
                    </p>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
                    <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                        <Button
                            variant="outline"
                            onClick={() => handleExport('csv')}
                            className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
                        >
                            <FileDown size={16} /><span>Export CSV</span>
                        </Button>
                    </Can>
                    <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                        <Button
                            onClick={openAdd}
                            className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            <Plus size={16} /><span>Create New Yard</span>
                        </Button>
                    </Can>
                </div>
            </div>

            {/* Open / Closed */}
            <Tabs value={status} onValueChange={(v) => setStatus(v as YardStatus)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="open" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Open
                    </TabsTrigger>
                    <TabsTrigger value="closed" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Closed Yards
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Search */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search yards..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {isClosed && filtered.length > 0 && (
                <p className="text-sm text-muted-foreground bg-muted/50 border border-border rounded-lg px-4 py-3">
                    Closed yards are read-only. Their orders, photos, signatures and audit history
                    stay fully accessible for record-keeping and dispute resolution.
                </p>
            )}

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                    {isClosed ? (
                        'No closed yards'
                    ) : branches.length === 0 ? (
                        <>
                            <p>No yards yet.</p>
                            <p className="text-sm mt-1">
                                A yard must belong to a branch — create one under Lists → Branches first.
                            </p>
                        </>
                    ) : (
                        'No yards found'
                    )}
                </div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((y) => (
                            <Card key={y.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Warehouse size={16} className="text-muted-foreground shrink-0" />
                                            <span className="font-medium truncate">{y.yardName}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1 font-mono">{y.yardCode}</p>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                                    <p>{y.address}</p>
                                    <p className="flex items-center gap-1.5"><MapPin size={14} />{y.branch?.name ?? '–'}</p>
                                    <p className="flex items-center gap-1.5">
                                        <User size={14} /><YardManagers managers={y.managers} />
                                    </p>
                                    {isClosed && y.closeReason && (
                                        <p className="text-xs pt-1">Closed: {y.closeReason}</p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    {isClosed ? (
                                        <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                                            <Button variant="outline" size="sm" onClick={() => handleReopen(y)}>
                                                <ArchiveRestore size={16} className="mr-1.5" />Reopen
                                            </Button>
                                        </Can>
                                    ) : (
                                        <>
                                            <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                                                <Button variant="outline" size="icon" onClick={() => openEdit(y)} aria-label="Manage yard">
                                                    <Edit size={16} />
                                                </Button>
                                            </Can>
                                            <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                                                <Button variant="outline" size="icon" onClick={() => setClosing(y)} aria-label="Close yard">
                                                    <Archive size={16} />
                                                </Button>
                                            </Can>
                                        </>
                                    )}
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Yard Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Code</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Branch</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Address</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Yard Managers</TableHead>
                                        {isClosed && (
                                            <TableHead className="font-semibold text-slate-900">Closed</TableHead>
                                        )}
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((y) => (
                                        <TableRow key={y.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Warehouse size={16} className="text-muted-foreground" />
                                                    {y.yardName}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-mono text-muted-foreground">{y.yardCode}</TableCell>
                                            <TableCell>{y.branch?.name ?? <span className="text-muted-foreground">–</span>}</TableCell>
                                            <TableCell className="whitespace-normal max-w-[240px]">{y.address}</TableCell>
                                            <TableCell><YardManagers managers={y.managers} /></TableCell>
                                            {isClosed && (
                                                <TableCell className="whitespace-normal max-w-[220px] text-sm text-muted-foreground">
                                                    {y.closeReason ?? '–'}
                                                    {y.closedBy?.name && (
                                                        <span className="block text-xs">by {y.closedBy.name}</span>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    {isClosed ? (
                                                        <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                                                            <Button variant="outline" size="sm" onClick={() => handleReopen(y)}>
                                                                <ArchiveRestore size={16} className="mr-1.5" />Reopen
                                                            </Button>
                                                        </Can>
                                                    ) : (
                                                        <>
                                                            <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                                                                <Button variant="outline" size="icon" onClick={() => openEdit(y)} aria-label="Manage yard">
                                                                    <Edit size={16} />
                                                                </Button>
                                                            </Can>
                                                            <Can I={ACTIONS.MANAGE} a={RESOURCES.STOCK_ADMIN}>
                                                                <Button variant="outline" size="icon" onClick={() => setClosing(y)} aria-label="Close yard">
                                                                    <Archive size={16} />
                                                                </Button>
                                                            </Can>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <CloseYardDialog
                open={Boolean(closing)}
                onOpenChange={(open) => { if (!open) setClosing(null); }}
                yard={closing}
                onClosed={fetchYards}
            />
        </div>
    );
}
